import { Component, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'

interface Props {
  widgetName?: string
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Error boundary that catches render errors in a single widget
 * and displays a fallback instead of crashing the entire dashboard.
 */
export default class WidgetErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-400 p-4">
          <AlertTriangle size={24} className="text-amber-400" />
          <p className="text-sm font-medium text-gray-500">
            {this.props.widgetName || 'Widget'} failed to render
          </p>
          <p className="text-xs text-gray-400 text-center max-w-xs">
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-1 px-2 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
          >
            Retry
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
